import { useEffect, useState } from "react";
import { ExternalLink, XIcon } from "lucide-react";
import { MessageResponse } from "@/components/ai-elements/message";
import { SeedForm } from "@/components/kb/SeedForm";
import { AdminAlert } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError } from "@/lib/api";
import {
  parseKbCitationPageUrl,
  type HermesCitationSource,
} from "@/lib/hermes-citations";
import {
  createKbApi,
  findSeedPageDef,
  type KbPage,
  type KbWiki,
} from "@/lib/kb-api";
import { seedFieldAnomaliesFromFrontmatter } from "@/lib/kb-page-status";
import { cn } from "@/lib/utils";

type HermesKbSourcePreviewDialogProps = {
  source: HermesCitationSource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function HermesKbSourcePreviewDialog({
  source,
  open,
  onOpenChange,
}: HermesKbSourcePreviewDialogProps) {
  const pageRef = parseKbCitationPageUrl(source.url);
  const accountId = pageRef?.accountId;
  const pageId = pageRef?.pageId;
  const seedField = pageRef?.seedField;
  const [page, setPage] = useState<KbPage | null>(null);
  const [wiki, setWiki] = useState<KbWiki | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !accountId || pageId == null) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(null);
    setWiki(null);

    const kb = createKbApi(accountId);
    void (async () => {
      try {
        const { page: loaded } = await kb.getPage(pageId);
        if (cancelled) return;
        setPage(loaded);

        if (loaded.is_seed) {
          const { wiki: loadedWiki } = await kb.getWiki(loaded.wiki_id);
          if (cancelled) return;
          setWiki(loadedWiki);
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "加载失败";
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, accountId, pageId]);

  // 深链字段：加载后滚到种子表单对应控件
  useEffect(() => {
    if (!open || !page?.is_seed || !seedField || loading) return;

    const id = `seed-field-${seedField}`;
    const timer = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") {
        el.focus({ preventScroll: true });
      }
    }, 80);

    return () => window.clearTimeout(timer);
  }, [open, page?.id, page?.is_seed, seedField, loading]);

  function openInNewWindow() {
    window.open(source.url, "_blank", "noopener,noreferrer");
  }

  const title = page?.title?.trim() || source.title?.trim() || "知识库页面";
  const subtitle = source.description?.trim() || null;
  const seedDef = page?.is_seed && wiki ? findSeedPageDef(wiki, page) : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          "flex h-[min(80vh,52rem)] w-[min(96vw,56rem)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none",
        )}
      >
        <DialogHeader className="shrink-0 flex-row items-start justify-between gap-3 space-y-0 border-b border-border px-6 py-4 text-left">
          <div className="min-w-0 flex-1 space-y-1">
            <DialogTitle className="truncate text-base">{title}</DialogTitle>
            {subtitle ? (
              <DialogDescription className="truncate">{subtitle}</DialogDescription>
            ) : (
              <DialogDescription className="sr-only">
                知识库页面预览
              </DialogDescription>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openInNewWindow}
            >
              <ExternalLink className="size-3.5" />
              新窗口打开
            </Button>
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="icon-sm" aria-label="关闭">
                <XIcon />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {!pageRef ? (
            <AdminAlert>无法识别该内部链接，请用「新窗口打开」查看。</AdminAlert>
          ) : null}
          {error ? <AdminAlert>{error}</AdminAlert> : null}
          {loading ? (
            <p className="text-sm text-muted-foreground">加载中…</p>
          ) : null}
          {!loading && page && !error ? (
            page.is_seed ? (
              <SeedForm
                seedDef={seedDef}
                structured={page.structured ?? {}}
                body={page.body ?? ""}
                disabled
                // 预览勿传 pageId，避免写回 knowledgeEntry 会话态
                fieldAnomalies={seedFieldAnomaliesFromFrontmatter(page.frontmatter)}
                onStructuredChange={() => undefined}
                onBodyChange={() => undefined}
              />
            ) : (
              <MarkdownPreview body={page.body ?? ""} />
            )
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MarkdownPreview({ body }: { body: string }) {
  if (!body.trim()) {
    return <p className="text-sm text-muted-foreground">暂无正文</p>;
  }

  return (
    <MessageResponse
      className={cn(
        "max-w-none text-sm leading-relaxed text-foreground",
        "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
        "[&_h1]:mb-4 [&_h1]:mt-0 [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:tracking-tight",
        "[&_h2]:mb-3 [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight",
        "[&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:text-lg [&_h3]:font-medium",
        "[&_p]:my-3 [&_ul]:my-3 [&_ol]:my-3 [&_li]:my-1",
        "[&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground",
        "[&_pre]:my-4 [&_table]:my-4 [&_table]:text-sm",
        "[&_a]:text-primary [&_a]:underline-offset-4 hover:[&_a]:underline",
      )}
      mode="static"
    >
      {body}
    </MessageResponse>
  );
}
